import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Input, Select, Tabs, Modal } from "antd";
import { PlusOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import _get from "lodash/get";
import Table from "components/common/Table";
import VoucherFonehouseColumn from "components/Columns/VoucherFonehouseColumn";
import {
  getVoucherListRequest,
  deleteVoucherRequest,
  setVoucherModal,
} from "providers/FonehouseVoucher/slice";
import CreateVoucherModal from "./CreateVoucherModal";
import VoucherUsageExpand from "./VoucherUsageExpand";

const { TabPane } = Tabs;
const { confirm } = Modal;

const VoucherList = () => {
  const dispatch = useDispatch();
  const docs = useSelector((state) =>
    _get(state, "voucherFonehouse.docs", [])
  );
  const totalDocs = useSelector((state) =>
    _get(state, "voucherFonehouse.totalDocs", 0)
  );
  const isLoading = useSelector((state) =>
    _get(state, "voucherFonehouse.isLoading", false)
  );
  const isModalVisible = useSelector((state) =>
    _get(state, "voucherFonehouse.isModalVisible", false)
  );
  const modalData = useSelector((state) =>
    _get(state, "voucherFonehouse.modalData", null)
  );

  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(
    (page = 1) => {
      const params = {
        page,
        limit: 10,
        keyword: keyword || undefined,
        status: statusFilter || undefined,
        expired: activeTab === "expired" ? true : undefined,
      };
      dispatch(getVoucherListRequest(params));
      setCurrentPage(page);
    },
    [dispatch, keyword, statusFilter, activeTab]
  );

  useEffect(() => {
    fetchData(1);
  }, [activeTab]);

  const handleSearch = () => {
    fetchData(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleEdit = (record) => {
    dispatch(setVoucherModal({ visible: true, data: record }));
  };

  const handleDelete = (record) => {
    confirm({
      title: "Xác nhận xóa voucher",
      icon: <ExclamationCircleOutlined />,
      content: `Bạn có chắc chắn muốn xóa voucher #${record.code}?`,
      okText: "Xóa",
      okType: "danger",
      cancelText: "Huỷ",
      onOk() {
        dispatch(deleteVoucherRequest({ id: record._id }));
      },
    });
  };

  const handleOpenCreateModal = () => {
    dispatch(setVoucherModal({ visible: true, data: null }));
  };

  const handleCloseModal = () => {
    dispatch(setVoucherModal({ visible: false, data: null }));
  };

  const handlePageChange = (page) => {
    fetchData(page);
  };

  const columns = [
    VoucherFonehouseColumn.code,
    VoucherFonehouseColumn.title,
    VoucherFonehouseColumn.condition,
    VoucherFonehouseColumn.discountValue,
    VoucherFonehouseColumn.dateRange,
    VoucherFonehouseColumn.usageDuration,
    VoucherFonehouseColumn.used,
    VoucherFonehouseColumn.description,
    VoucherFonehouseColumn.actions(handleEdit, handleDelete),
  ];

  return (
    <div style={{ padding: "0 24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0, whiteSpace: "nowrap" }}>Voucher Fonehouse</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateModal} style={{ width: "auto", flexShrink: 0 }}>
          Tạo voucher
        </Button>
      </div>

      {/* Search bar */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Input.Search
          placeholder="Tìm kiếm"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={handleSearch}
          onKeyPress={handleKeyPress}
          style={{ maxWidth: 400 }}
          allowClear
          enterButton="Tìm"
        />
        <Select
          placeholder="Trạng thái"
          value={statusFilter}
          onChange={(val) => setStatusFilter(val)}
          style={{ width: 160 }}
          allowClear
        >
          <Select.Option value="ACTIVE">Active</Select.Option>
          <Select.Option value="INACTIVE">Inactive</Select.Option>
          <Select.Option value="EXPIRED">Expired</Select.Option>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Tất cả" key="all" />
        <TabPane tab="Đã hết hạn" key="expired" />
      </Tabs>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={docs}
        rowKey={(data) => data._id}
        loading={isLoading}
        total={totalDocs}
        current={currentPage}
        onChange={(pagination) => handlePageChange(pagination.current)}
        scroll={{ x: 1200 }}
        expandable={{
          expandedRowRender: (record) => <VoucherUsageExpand record={record} />,
          rowExpandable: () => true,
        }}
      />

      {/* Create/Edit Modal */}
      <CreateVoucherModal
        visible={isModalVisible}
        onClose={handleCloseModal}
        editData={modalData}
      />
    </div>
  );
};

export default VoucherList;
